import { DetailRequirement, PrgDetail, ResourceDetail, TraitId } from '@cibo/core'
import { ResponsiveDialog } from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import {
  Backdrop,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import { Field, FieldProps, Form, Formik, FormikProps, getIn } from 'formik'
import { useSnackbar } from 'notistack'
import { ascend, pick, propOr } from 'ramda'
import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GenericDetailEditor, GenericEditorSkeleton, ITraitFeatureById, TraitFeature } from '..'
import { useFields } from '../../..'
import { OverrideWarning } from '../OverrideWarning'
import { useSaveBulkDetails } from '../queries'
import { findInitialValuesDetail } from '../utils'
import { BulkEditHeader } from './BulkEditHeader'
import { EditYearAccordionSummary } from './EditYearAccordionSummary'
import Yup from './yupWithTuple'

type EditMultipleFieldsFormProps = {
  requestClose(): void
  resourceIds?: string[]
  traitId?: TraitId
  onSuccess?: () => void
  requirements?: DetailRequirement[]
  filterInputs?: PrgDetail
}

export const EditMultipleFieldsForm = ({
  requestClose,
  resourceIds = [],
  traitId,
  onSuccess,
  requirements,
  filterInputs,
}: EditMultipleFieldsFormProps) => {
  const { t } = useTranslation('@cibo/landmanager/DetailEditing')
  const { enqueueSnackbar } = useSnackbar()

  // any requirement year indicates the trait is yearly
  const yearRequired = !!requirements?.[0].year

  const sortedRequirements = useMemo<DetailRequirement[]>(
    () => requirements?.sort(ascend(propOr(0, 'year'))) || [],
    [requirements]
  )

  const [expanded, setExpanded] = useState<number | false>(false)
  useEffect(() => {
    sortedRequirements && sortedRequirements.length > 0
      ? setExpanded(sortedRequirements[0].year || 0)
      : setExpanded(false)
  }, [traitId])
  const handleChange = (panel: number) => (event: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  const fieldModels = useFields(resourceIds)
  const saveBulkDetails = useSaveBulkDetails()

  useEffect(() => {
    if (resourceIds) {
      saveBulkDetails.reset()
    }
  }, [resourceIds.join()])

  const Trait = TraitFeature.forTraitId(traitId)
  const { t: traitT } = useTranslation(Trait?.ns)

  const initialValues: { details: Array<ResourceDetail> } = useMemo(() => {
    if (!!Trait && !!traitId && !!fieldModels.data?.[0]) {
      const values: any = { details: [] }
      if (!yearRequired) {
        values.details.push({
          input: (Trait as ITraitFeatureById).initialValues({
            fieldModels: fieldModels.data ?? [],
          }),
          traitId,
        })
      } else {
        values.details = sortedRequirements.map(({ year }) => ({
          traitId,
          year,
          input: Trait.initialValues({
            fieldModels: fieldModels.data ?? [],
            year,
          }),
        }))
      }
      return values
    }
    return {}
  }, [sortedRequirements, Trait, traitId, fieldModels.isFetching])

  // immutableYears only provides accurate immutable years when editing a single field.
  const immutableYears = useMemo(() => {
    if (!Trait || !traitId || !fieldModels.data?.[0] || !sortedRequirements) {
      return {}
    }

    let values: any = []
    if (!yearRequired) {
      values.push({
        immutable: findInitialValuesDetail(traitId)({
          fieldModels: fieldModels.data ?? [],
        })?.immutable,
        traitId,
      })
    } else {
      values = sortedRequirements.map(({ year }) => ({
        traitId,
        year,
        immutable: findInitialValuesDetail(traitId)({
          fieldModels: fieldModels.data ?? [],
          year,
        })?.immutable,
      }))
    }
    return values.filter((a: { immutable?: boolean }) => a.immutable)
  }, [sortedRequirements, Trait, traitId, fieldModels.isFetching])

  const validationSchema = useMemo(() => {
    if (!!traitId && !!Trait?.validationSchema) {
      return Yup.object().shape({
        // @ts-ignore we added tuple to the Yup
        details: Yup.array().tuple(
          // The "tuple" validator enables yup to check the array length
          // and validate a unique schema for each item in the list.
          sortedRequirements.map(requirement =>
            Yup.object().shape({
              // @ts-ignore this is checked to exist above
              input: Trait.validationSchema({ requirement, t: traitT }),
            })
          )
        ),
      })
    }
  }, [sortedRequirements, Trait, traitId, traitT])

  if (!traitId) {
    return null
  }

  const DetailEditor = Trait ? Trait.editor : GenericDetailEditor
  const defaultYear = new Date().getFullYear()

  const handleSubmit = ({
    resetForm,
    values: { details },
  }: Pick<FormikProps<typeof initialValues>, 'values' | 'resetForm'>) => {
    if (!resourceIds || !traitId || !sortedRequirements) {
      return
    }

    const detailValues = details.map(detail =>
      Trait?.detailFromEditorValues
        ? Trait?.detailFromEditorValues(
            { input: detail.input },
            { ...pick(['traitId', 'year'], detail), dataType: 'field' }
          )
        : detail
    )

    return saveBulkDetails
      .mutateAsync({
        resourceIds,
        details: detailValues,
      })
      .then(() => {
        requestClose()
        enqueueSnackbar(
          t('bulkUpdateSuccess', {
            trait: traitT(traitId, { ns: 'traits' }),
            count: resourceIds?.length,
          }),
          { variant: 'success' }
        )
        resetForm()
        onSuccess && onSuccess()
      })
  }

  if (!resourceIds?.length || !sortedRequirements) return null

  return (
    /**
     * You may be scratching your head looking at this component structure.  Here's the deal:
     * MUI Dialog scroll behavior requires that the <Dialog* /> compoonents be direct children of the <Dialog /> component.
     * The <DialogActions /> need access to the Formik state, but we don't want to render the Formik form until the fieldModels
     * have loaded, this means <Formik/> needs to wrap the entire dialog.  However, we want to show _something_ is happening when
     * the user clicks edit, so we disable the backdrop on the dialog and render a shared <Backdrop /> around the loading / loaded states.
     */
    <Backdrop open={true} sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}>
      {fieldModels.isFetching ? (
        <CircularProgress />
      ) : (
        <Formik
          onSubmit={() => undefined}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnMount
          enableReinitialize
        >
          {({ errors, values, dirty, touched, setSubmitting, resetForm }) => {
            //we don't use the form's isValid because it considers untouched fields, which we don't want
            const hasErrors =
              !Array.isArray(touched?.details) ||
              (Array.isArray(errors?.details) &&
                errors.details?.some(
                  (error: any, index: number) => !!error && !!touched?.details?.[index]
                ))
            return (
              <Form>
                <ResponsiveDialog
                  open={!!resourceIds}
                  onClose={requestClose}
                  sx={{
                    '& .MuiInputLabel-root': { whiteSpace: 'unset' },
                  }}
                  maxWidth="sm"
                  fullWidth
                  hideBackdrop
                >
                  <DialogTitle>
                    {t('editSelectedFields', { traitId, count: fieldModels.data?.length })}
                  </DialogTitle>

                  <DialogContent>
                    {Trait?.forbidBulkEdit && fieldModels.data && fieldModels.data.length > 1 ? (
                      <Typography>{t('cannotBulkEdit')}</Typography>
                    ) : (
                      <Stack spacing={2}>
                        <BulkEditHeader fieldModels={fieldModels.data} />

                        {yearRequired ? (
                          sortedRequirements.map(
                            (requirement: DetailRequirement, index: number) => {
                              const yearIsImmutable = immutableYears.find(
                                (a: { year: number }) => a.year === requirement.year
                              )

                              return (
                                <Field key={`details.${index}`} name={`details.${index}`}>
                                  {({
                                    field: {
                                      name,
                                      value: { year },
                                      value,
                                    },
                                    meta: { touched, error, initialValue },
                                  }: FieldProps<any>) => {
                                    const isFieldDirty =
                                      JSON.stringify(initialValue) !== JSON.stringify(value)
                                    return (
                                      <Accordion
                                        key={index}
                                        expanded={expanded === index && !yearIsImmutable}
                                        onChange={handleChange(index)}
                                        data-testid={`expand-${traitId}-${index}`}
                                        disabled={yearIsImmutable}
                                        sx={{
                                          '&.Mui-disabled': {
                                            backgroundColor: 'rgba(0,0,0,0.05)',
                                          },
                                        }}
                                      >
                                        <EditYearAccordionSummary
                                          year={year}
                                          fieldModels={fieldModels.data || []}
                                          traitId={traitId}
                                          isFieldDirty={isFieldDirty}
                                          touched={touched}
                                          error={error}
                                          yearIsImmutable={yearIsImmutable}
                                        />
                                        <AccordionDetails>
                                          {fieldModels.isFetching ? (
                                            <GenericEditorSkeleton />
                                          ) : (
                                            <DetailEditor
                                              traitId={traitId}
                                              name={`${name}.input`}
                                              fieldModels={fieldModels.data || []}
                                              year={year as number}
                                              requirement={requirement}
                                              filterInputs={filterInputs}
                                            />
                                          )}
                                        </AccordionDetails>
                                      </Accordion>
                                    )
                                  }}
                                </Field>
                              )
                            }
                          )
                        ) : fieldModels.isFetching ? (
                          <GenericEditorSkeleton />
                        ) : (
                          <>
                            <OverrideWarning
                              fieldModels={fieldModels.data || []}
                              traitId={traitId}
                            />

                            <DetailEditor
                              name={`details.0.input`}
                              traitId={traitId}
                              fieldModels={fieldModels.data || []}
                              requirement={sortedRequirements[0]}
                              year={defaultYear}
                            />
                          </>
                        )}
                      </Stack>
                    )}
                  </DialogContent>

                  <DialogActions>
                    <Button onClick={requestClose}>{t('cancel')}</Button>
                    <LoadingButton
                      variant="contained"
                      onClick={() => {
                        setSubmitting(true)

                        handleSubmit({
                          values: {
                            details: values.details.filter((detail, index) =>
                              getIn(touched, `details.${index}`)
                            ),
                          },
                          resetForm,
                        })
                      }}
                      disabled={!dirty || hasErrors}
                      loading={saveBulkDetails.isLoading}
                      data-testid="bulk-save"
                    >
                      {t('submit')}
                    </LoadingButton>
                  </DialogActions>
                </ResponsiveDialog>
              </Form>
            )
          }}
        </Formik>
      )}
    </Backdrop>
  )
}
